import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableRow,
    TableHead,
    CircularProgress,
} from "@mui/material";
import { AddCircle, Delete, Edit, Save } from "@mui/icons-material";
import axios from "axios";
import { BACKEND_BASE_URL } from "../../../../config";
import api from "../../../../components/api";

const CorrectionDictionaryDialog = ({ open, onClose, row }) => {
    const [corrections, setCorrections] = useState([]);
    const [editIndex, setEditIndex] = useState(null);
    const [loadingIndex, setLoadingIndex] = useState(null);
    const [originalEntry, setOriginalEntry] = useState(null);


    useEffect(() => {
        if (row?.correction_mappings) {
            setCorrections(row.correction_mappings);
            setEditIndex(null);
        }
    }, [row]);

    const handleFieldChange = (index, key, value) => {
        const updated = [...corrections];
        updated[index][key] = value;
        setCorrections(updated);
    };

    const handleAdd = () => {
        const newEntry = { wrong: "", right: "" };
        setCorrections([...corrections, newEntry]);
        setEditIndex(corrections.length);
        setOriginalEntry(null); // clear original, since it's a new entry
    };


    const handleSaveEdit = async (index) => {
        const updatedEntry = corrections[index];

        try {
            setLoadingIndex(index);

            if (originalEntry) {
                // Editing existing
                await api.patch(
                    `${BACKEND_BASE_URL}/api/urltables/${row.id}/edit_correction/`,
                    {
                        original: originalEntry,
                        updated: updatedEntry,
                    }
                );
            } else {
                // Adding new
                await api.post(
                    `${BACKEND_BASE_URL}/api/urltables/${row.id}/add_correction/`,
                    updatedEntry
                );
            }

            setEditIndex(null);
            setOriginalEntry(null);
        } catch (err) {
            console.error("Save failed:", err);
        } finally {
            setLoadingIndex(null);
        }
    };


    const handleDelete = async (index) => {
        const entry = corrections[index];
        try {
            setLoadingIndex(index);
            await api.delete(`${BACKEND_BASE_URL}/api/urltables/${row.id}/delete_correction/`, {
                data: entry,
            });
            const updatedList = corrections.filter((_, i) => i !== index);
            setCorrections(updatedList);
            if (editIndex === index) setEditIndex(null);
        } catch (err) {
            console.error("Delete failed:", err);
        } finally {
            setLoadingIndex(null);
        }
    };


    const handleEditToggle = (index) => {
        setOriginalEntry({ ...corrections[index] }); // capture before edit
        setEditIndex(index);
    };


    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Correction Dictionary</DialogTitle>
            <DialogContent dividers>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Wrong</strong></TableCell>
                            <TableCell><strong>Right</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {corrections.map((entry, index) => (
                            <TableRow key={index}>
                                <TableCell>
                                    {editIndex === index ? (
                                        <TextField
                                            value={entry.wrong}
                                            onChange={(e) => handleFieldChange(index, "wrong", e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    ) : (
                                        entry.wrong
                                    )}
                                </TableCell>
                                <TableCell>
                                    {editIndex === index ? (
                                        <TextField
                                            value={entry.right}
                                            onChange={(e) => handleFieldChange(index, "right", e.target.value)}
                                            size="small"
                                            fullWidth
                                        />
                                    ) : (
                                        entry.right
                                    )}
                                </TableCell>
                                <TableCell align="right">
                                    {editIndex === index ? (
                                        <IconButton size="small" onClick={() => handleSaveEdit(index)} disabled={loadingIndex === index}>
                                            {loadingIndex === index ? <CircularProgress size={20} /> : <Save fontSize="small" />}
                                        </IconButton>
                                    ) : (
                                        <IconButton size="small" onClick={() => handleEditToggle(index)}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    )}
                                    <IconButton size="small" onClick={() => handleDelete(index)} disabled={loadingIndex === index}>
                                        {loadingIndex === index ? <CircularProgress size={20} /> : <Delete fontSize="small" />}
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>

                <Button
                    onClick={handleAdd}
                    startIcon={<AddCircle />}
                    variant="text"
                    sx={{ mt: 2 }}
                >
                    Add Mapping
                </Button>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CorrectionDictionaryDialog;
